import { tidyAddressText, type AddressInput } from '@/domain';

/**
 * A doctor's address as its form holds it. The request of 6 October 2026
 * (fifth), section 2. The nine parts a form sends, every one of them present:
 * the country always, and each of the rest as typed or chosen, or null.
 */
export type AddressParts = Required<AddressInput>;

/**
 * Whether nothing of an address is filled in: only the country it would be
 * in. That is no address at all, which a doctor is allowed to have, and it is
 * what telling the server to clear one looks like in the form.
 */
export function addressEmpty(parts: AddressInput): boolean {
  return [parts.region, parts.city, parts.area, parts.line1, parts.line2, parts.postalCode].every(
    (part) => tidyAddressText(part) === null,
  );
}

/**
 * The address moved to another country. The same request: changing the
 * country clears the region, the city, the area and the postal code, so no
 * city is kept in a country it is not in and no postal code is held to
 * another country's pattern. The street lines are the person's own words and
 * stay.
 */
export function inCountry(parts: AddressParts, country: string): AddressParts {
  return {
    ...parts,
    country,
    region: null,
    regionCode: null,
    city: null,
    cityId: null,
    area: null,
    postalCode: null,
  };
}

/** No address, in the country the form is on. */
export function cleared(parts: AddressParts): AddressParts {
  return { ...inCountry(parts, parts.country ?? ''), line1: null, line2: null };
}

/** What was typed in a box as a part holds it: the text, or null for an empty box. */
export function partOf(text: string): string | null {
  return text === '' ? null : text;
}
